import { Decimal } from "decimal.js";
Decimal.set({ precision: 32, rounding: Decimal.ROUND_HALF_UP });
export function money(value) {
    return new Decimal(value).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
}
export function moneyString(value) {
    return money(value).toFixed(2);
}
export function parseSwedishDecimal(input) {
    const compact = input.trim().replace(/[\s\u00a0\u202f]/g, "");
    if (!compact)
        throw new Error("Belopp saknas");
    if (!/^[+-]?[\d.,]+$/.test(compact))
        throw new Error("Ogiltigt belopp");
    const comma = compact.lastIndexOf(",");
    const dot = compact.lastIndexOf(".");
    let normalized = compact;
    if (comma >= 0 && dot >= 0) {
        const decimalMark = comma > dot ? "," : ".";
        const thousandsMark = decimalMark === "," ? "." : ",";
        normalized = compact.replaceAll(thousandsMark, "").replace(decimalMark, ".");
    }
    else if (comma >= 0) {
        normalized = compact.replaceAll(".", "").replace(",", ".");
    }
    else {
        normalized = compact.replaceAll(",", "");
    }
    const parsed = new Decimal(normalized);
    if (!parsed.isFinite())
        throw new Error("Ogiltigt belopp");
    return parsed;
}
export function parseSwedishMoney(input) {
    return money(parseSwedishDecimal(input));
}
export function normalizeSwedishMoney(input) {
    return parseSwedishMoney(input).toFixed(2);
}
export function tryNormalizeSwedishMoney(input) {
    try {
        return normalizeSwedishMoney(input);
    }
    catch {
        return null;
    }
}
export function sumMoney(values) {
    return moneyString(values.reduce((sum, value) => sum.add(parseSwedishMoney(value || "0")), new Decimal(0)));
}
export function subtractMoney(left, right) {
    return moneyString(parseSwedishMoney(left).sub(parseSwedishMoney(right)));
}
export function deriveInvoiceTotal(netAmount, vatAmount) {
    const net = tryNormalizeSwedishMoney(netAmount);
    const vat = tryNormalizeSwedishMoney(vatAmount);
    return net === null || vat === null ? null : sumMoney([net, vat]);
}
export function summarizePostingLines(lines) {
    const debits = lines.map((line) => tryNormalizeSwedishMoney(line.debit));
    const credits = lines.map((line) => tryNormalizeSwedishMoney(line.credit));
    const invalid = [...debits, ...credits].some((value) => value === null);
    const debit = sumMoney(debits.filter((value) => value !== null));
    const credit = sumMoney(credits.filter((value) => value !== null));
    return { debit, credit, difference: invalid ? null : subtractMoney(debit, credit) };
}
export function calculateVat(net, ratePercent) {
    return moneyString(parseSwedishMoney(net).mul(new Decimal(ratePercent)).div(100));
}
export function compareMoney(left, right) {
    return parseSwedishMoney(left).cmp(parseSwedishMoney(right));
}
export function isZeroMoney(value) {
    const normalized = tryNormalizeSwedishMoney(value);
    return normalized !== null && new Decimal(normalized).isZero();
}
export function isPositiveMoney(value) {
    const normalized = tryNormalizeSwedishMoney(value);
    return normalized !== null && new Decimal(normalized).greaterThan(0);
}
export function allocateProportionally(totalMinor, weights) {
    if (totalMinor < 0n || weights.some((weight) => weight < 0n))
        throw new Error("Negative allocation is not supported");
    const weightTotal = weights.reduce((sum, value) => sum + value, 0n);
    if (weightTotal === 0n)
        return weights.map(() => 0n);
    // Largest-remainder allocation is deterministic, conserves every öre and
    // cannot make the final bucket negative (independent half-up rounding can).
    const shares = weights.map((weight) => (totalMinor * weight) / weightTotal);
    const remainders = weights.map((weight, index) => ({
        index,
        remainder: (totalMinor * weight) % weightTotal,
    }));
    let residual = totalMinor - shares.reduce((sum, value) => sum + value, 0n);
    remainders.sort((left, right) => left.remainder === right.remainder
        ? left.index - right.index
        : left.remainder > right.remainder
            ? -1
            : 1);
    for (const item of remainders) {
        if (residual === 0n)
            break;
        shares[item.index] = (shares[item.index] ?? 0n) + 1n;
        residual -= 1n;
    }
    return shares;
}
//# sourceMappingURL=money.js.map